import { useCallback, useEffect, useRef, useState } from 'react'
import type { Toast } from './ToastContext'

interface ToastContainerProps {
  toasts: Toast[]
  onDismiss: (id: string) => void
}

// --- Inline SVG Icons ---

function SuccessIcon() {
  return (
    <svg
      className="h-5 w-5 shrink-0 text-[color:var(--toast-accent)]"
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
        clipRule="evenodd"
      />
    </svg>
  )
}

function ErrorIcon() {
  return (
    <svg
      className="h-5 w-5 shrink-0 text-[color:var(--toast-accent)]"
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
        clipRule="evenodd"
      />
    </svg>
  )
}

function WarningIcon() {
  return (
    <svg
      className="h-5 w-5 shrink-0 text-[color:var(--toast-accent)]"
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.168 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
        clipRule="evenodd"
      />
    </svg>
  )
}

function InfoIcon() {
  return (
    <svg
      className="h-5 w-5 shrink-0 text-[color:var(--toast-accent)]"
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z"
        clipRule="evenodd"
      />
    </svg>
  )
}

function DismissIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
    </svg>
  )
}

// --- Status accent map ---

const accentColorMap: Record<Toast['type'], string> = {
  success: '[--toast-accent:var(--toast-success-accent)]',
  error: '[--toast-accent:var(--toast-error-accent)]',
  warning: '[--toast-accent:var(--toast-warning-accent)]',
  info: '[--toast-accent:var(--toast-info-accent)]',
}

const iconMap: Record<Toast['type'], () => JSX.Element> = {
  success: SuccessIcon,
  error: ErrorIcon,
  warning: WarningIcon,
  info: InfoIcon,
}

// --- Individual toast item with enter/exit animation ---

function ToastItem({
  toast,
  onDismiss,
  paused,
}: {
  toast: Toast
  onDismiss: (id: string) => void
  paused: boolean
}) {
  const [visible, setVisible] = useState(false)
  const duration = toast.duration ?? 0
  const timeoutRef = useRef<number | null>(null)
  const cycleStartedAtRef = useRef(0)
  const cycleDurationMsRef = useRef(duration)
  const remainingMsRef = useRef(duration)
  const pausedRef = useRef(false)
  const [remainingMs, setRemainingMs] = useState(duration)

  // Trigger enter animation on mount
  useEffect(() => {
    // Small delay to allow the browser to paint the initial state first
    const frame = requestAnimationFrame(() => {
      setVisible(true)
    })
    return () => cancelAnimationFrame(frame)
  }, [])

  const clearDismissTimeout = useCallback(() => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  const scheduleDismiss = useCallback(
    (delayMs: number) => {
      if (delayMs <= 0) {
        onDismiss(toast.id)
        return
      }
      clearDismissTimeout()
      cycleStartedAtRef.current = performance.now()
      cycleDurationMsRef.current = delayMs
      remainingMsRef.current = delayMs
      setRemainingMs(delayMs)
      timeoutRef.current = window.setTimeout(() => onDismiss(toast.id), delayMs)
    },
    [clearDismissTimeout, onDismiss, toast.id]
  )

  const pauseDismiss = useCallback(() => {
    if (duration <= 0) return
    const elapsed = performance.now() - cycleStartedAtRef.current
    const nextRemaining = Math.max(0, cycleDurationMsRef.current - elapsed)
    remainingMsRef.current = nextRemaining
    setRemainingMs(nextRemaining)
    clearDismissTimeout()
  }, [clearDismissTimeout, duration])

  const resumeDismiss = useCallback(() => {
    if (duration <= 0) return
    scheduleDismiss(remainingMsRef.current)
  }, [duration, scheduleDismiss])

  useEffect(() => {
    if (duration <= 0) return

    scheduleDismiss(duration)
    return clearDismissTimeout
  }, [clearDismissTimeout, duration, scheduleDismiss])

  useEffect(() => {
    if (duration <= 0 || paused === pausedRef.current) return

    pausedRef.current = paused
    if (paused) {
      pauseDismiss()
    } else {
      resumeDismiss()
    }
  }, [duration, pauseDismiss, paused, resumeDismiss])

  useEffect(() => {
    if (duration <= 0) return

    const updateRemainingMs = () => {
      if (paused) {
        setRemainingMs(remainingMsRef.current)
        return
      }
      const elapsed = performance.now() - cycleStartedAtRef.current
      const nextRemaining = Math.max(0, cycleDurationMsRef.current - elapsed)
      setRemainingMs(nextRemaining)
    }

    updateRemainingMs()
    const interval = window.setInterval(updateRemainingMs, 250)
    return () => window.clearInterval(interval)
  }, [duration, paused])

  const Icon = iconMap[toast.type]
  const remainingSeconds = Math.max(0, Math.ceil(remainingMs / 1000))
  const compactCountdownLabel = `${remainingSeconds}s`

  return (
    <div
      role="alert"
      className={`
        relative overflow-hidden rounded-md border-2 border-[color:var(--toast-accent)] bg-muted p-3.5 pr-11 shadow-lg
        w-[min(calc(100vw-2rem),440px)]
        flex items-start gap-3
        transition-all duration-300 ease-out
        ${accentColorMap[toast.type]}
        ${visible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
      `}
    >
      <Icon />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold leading-5 text-default">{toast.title}</p>
        {toast.message && <p className="mt-0.5 text-sm leading-5 text-subtle">{toast.message}</p>}
        {toast.actions && toast.actions.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {toast.actions.map((action) => (
              <button
                key={action.label}
                type="button"
                className="rounded-md border border-default bg-[color:var(--surface-default)] px-2 py-0.5 text-xs font-medium text-subtle transition-colors hover:bg-[color:var(--bg-emphasis)] hover:text-default"
                onClick={() => {
                  action.onClick()
                  onDismiss(toast.id)
                }}
              >
                {action.label}
              </button>
            ))}
          </div>
        )}
        {duration > 0 && (
          <p className="mt-3 text-xs leading-4 text-muted">
            {paused
              ? `Closing in ${compactCountdownLabel} after cursor leaves`
              : `Closing in ${compactCountdownLabel}`}
          </p>
        )}
      </div>
      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        className="absolute right-3 top-3 flex h-6 w-6 items-center justify-center rounded text-muted transition-colors hover:bg-[color:var(--bg-emphasis)] hover:text-subtle"
        aria-label="Dismiss notification"
      >
        <DismissIcon />
      </button>
      {duration > 0 && (
        <div
          className="toast-countdown-bar absolute bottom-0 left-0 h-[2px] bg-[color:var(--toast-accent)]"
          style={{
            animationDuration: `${duration}ms`,
            animationPlayState: paused ? 'paused' : 'running',
          }}
          aria-hidden="true"
        />
      )}
    </div>
  )
}

// --- Container ---

function ToastLayout({ toasts, onDismiss }: ToastContainerProps) {
  const [paused, setPaused] = useState(false)

  return (
    <div className="pointer-events-none fixed bottom-20 right-4 z-[var(--layer-toast)]">
      <div
        className="pointer-events-auto flex flex-col gap-2"
        aria-live="polite"
        aria-label="Notifications"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocus={() => setPaused(true)}
        onBlur={(event) => {
          const nextFocusTarget = event.relatedTarget as Node | null
          if (!event.currentTarget.contains(nextFocusTarget)) {
            setPaused(false)
          }
        }}
      >
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} paused={paused} />
        ))}
      </div>
    </div>
  )
}

export function ToastContainer({ toasts, onDismiss }: ToastContainerProps) {
  if (toasts.length === 0) return null

  return <ToastLayout toasts={toasts} onDismiss={onDismiss} />
}
